// client/src/components/admin/analytics/EventsTableSection.js
import React from 'react'
import PropTypes from 'prop-types'
import EventActionsDropdown from '../EventActionsDropdown'
import MobileEventList from '../MobileEventList'
import {
  formatDateTime,
  getEventTypeIcon,
  getTagCategory,
  getTagCategoryColor,
} from '../../../utils/analyticsUtils'

/**
 * Events table section component with filtering and pagination.
 * Displays telemetry events in a responsive table/list view.
 *
 * @param {Array} events - Array of event objects to display
 * @param {Boolean} isLoading - Initial loading state
 * @param {Boolean} isLoadingMore - Pagination loading state
 * @param {Boolean} hasMore - Whether more events are available
 * @param {Function} onLoadMore - Handler for loading more events
 * @param {Function} onEventClick - Handler for event selection (view details)
 * @param {Function} onRefresh - Handler for refresh action
 * @param {Function} onCopyId - Handler for copying event ID
 * @param {Function} onCopyPayload - Handler for copying event payload
 * @param {Function} onJumpToUser - Handler for navigating to user
 * @param {Object} filterControls - Filter control props (optional)
 */
function EventsTableSection({
  events,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onEventClick,
  onCopyId,
  onCopyPayload,
  onJumpToUser,
  filterControls = null,
}) {
  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title mb-0">🕒 Recent Events</h5>
                <small className="text-muted" data-testid="events-subtitle">
                  {filterControls?.subtitle || 'Last 100 system events'}
                </small>
              </div>

              {/* Filter Controls (if provided) */}
              {filterControls && (
                <div className="d-flex align-items-center gap-2 events-controls">
                  {filterControls.content}
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            {/* Mobile Event List */}
            <MobileEventList
              events={events}
              eventsLoading={isLoading}
              onViewDetails={onEventClick}
              onCopyId={onCopyId}
              onCopyPayload={onCopyPayload}
              onJumpToUser={onJumpToUser}
              getEventTypeIcon={getEventTypeIcon}
              getTagCategory={getTagCategory}
              getTagCategoryColor={getTagCategoryColor}
              formatDateTime={formatDateTime}
            />

            {/* Desktop Table View */}
            {isLoading ? (
              <div
                className="d-none d-md-block text-center py-4"
                data-testid="events-loading"
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading events...</span>
                </div>
              </div>
            ) : events.length > 0 ? (
              <div className="table-responsive d-none d-md-block">
                <table
                  className="table table-sm table-hover"
                  data-testid="events-table"
                >
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '60px' }}>Type</th>
                      <th style={{ width: '200px' }}>Event</th>
                      <th style={{ width: '150px' }}>Page</th>
                      <th style={{ width: '100px' }}>User</th>
                      <th style={{ width: '150px' }}>Timestamp</th>
                      <th>Metadata</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, index) => {
                      const category = getTagCategory(event.tag)
                      return (
                        <tr
                          key={
                            event.id ??
                            `${event.event_type}-${event.timestamp}-${index}`
                          }
                          className="event-row"
                          data-testid={`event-row-${index}`}
                        >
                          <td>
                            <span title={event.event_type}>
                              {getEventTypeIcon(event.event_type)}
                            </span>
                          </td>
                          <td>
                            <div>
                              <span
                                className={`badge bg-${getTagCategoryColor(
                                  category,
                                )} mb-1`}
                                style={{ fontSize: '0.7rem' }}
                              >
                                {category}
                              </span>
                              <br />
                              <code style={{ fontSize: '0.8rem' }}>
                                {event.tag}
                              </code>
                            </div>
                          </td>
                          <td>
                            <code style={{ fontSize: '0.8rem' }}>
                              {event.page || '-'}
                            </code>
                          </td>
                          <td>
                            {event.user_id ? (
                              <span
                                className="badge bg-success user-badge-popover"
                                data-user-id={event.user_id}
                                tabIndex={0}
                                role="button"
                                aria-label={`View details for user ${event.user_id}`}
                              >
                                {event.user_id}
                              </span>
                            ) : (
                              <span className="badge bg-secondary">
                                Anonymous
                              </span>
                            )}
                          </td>
                          <td>
                            <small>{formatDateTime(event.timestamp)}</small>
                          </td>
                          <td>
                            {event.metadata &&
                            Object.keys(event.metadata).length > 0 ? (
                              <details>
                                <summary className="btn btn-sm btn-outline-secondary">
                                  View
                                </summary>
                                <pre className="mt-2 small">
                                  {JSON.stringify(event.metadata, null, 2)}
                                </pre>
                              </details>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </td>
                          <td>
                            <EventActionsDropdown
                              event={event}
                              onViewDetails={onEventClick}
                              onCopyId={onCopyId}
                              onCopyPayload={onCopyPayload}
                              onJumpToUser={onJumpToUser}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className="d-none d-md-block text-center text-muted py-4"
                data-testid="events-empty"
              >
                <p>No recent events found</p>
              </div>
            )}

            {/* Pagination Controls */}
            {!isLoading && events.length > 0 && (
              <div
                className="mt-3 text-center"
                data-testid="pagination-controls"
              >
                <div className="mb-2">
                  <small className="text-muted">
                    Showing {events.length} event
                    {events.length !== 1 ? 's' : ''}
                  </small>
                </div>
                {hasMore && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={onLoadMore}
                    disabled={isLoadingMore}
                    data-testid="load-more-button"
                  >
                    {isLoadingMore ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading...
                      </>
                    ) : (
                      <>Load More Events</>
                    )}
                  </button>
                )}
                {!hasMore && events.length >= 100 && (
                  <div className="text-muted" data-testid="no-more-events">
                    <small>No more events to load</small>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

EventsTableSection.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      event_type: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
      page: PropTypes.string,
      user_id: PropTypes.number,
      timestamp: PropTypes.string.isRequired,
      metadata: PropTypes.object,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onEventClick: PropTypes.func.isRequired,
  onCopyId: PropTypes.func.isRequired,
  onCopyPayload: PropTypes.func.isRequired,
  onJumpToUser: PropTypes.func.isRequired,
  filterControls: PropTypes.shape({
    subtitle: PropTypes.string,
    content: PropTypes.node,
  }),
}

export default EventsTableSection
